'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { AppVideo } from '@/app/components/media/app-video';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { useSpartanProductTopNavigationMediaPause } from '@/app/lcv/components/sections/spartan/products/shared/hooks/useTopNavigationMediaPause';
import { getSpartanVideoVisibilityThreshold, SPARTAN_IN_VIEW_VIDEO_DEFAULT_SIZES, SPARTAN_IN_VIEW_VIDEO_IN_VIEW_OPTIONS, SPARTAN_IN_VIEW_VIDEO_TRACKING_PREFIX } from '@/app/lcv/components/sections/spartan/products/shared/in-view-video/constants';
import type { SpartanInViewVideoHandle, SpartanInViewVideoProps } from '@/app/lcv/components/sections/spartan/products/shared/in-view-video/types';
import { usePageActivity } from '@/app/lcv/components/sections/spartan/shared/hooks/usePageActivity';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
import { playVideoWithPromiseHandle } from '@/app/utils/video';
const clampVisibilityThreshold = (threshold: number): number => Math.min(1, Math.max(0, threshold));
const VISIBILITY_RATIO_THRESHOLDS = [0.01, 0.1, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
export const SpartanInViewVideo = forwardRef<SpartanInViewVideoHandle, SpartanInViewVideoProps>(function SpartanInViewVideo({
  src,
  desktopMedia,
  image,
  index = 0,
  active = true,
  paused = false,
  wrapperClassName,
  className,
  sizes = SPARTAN_IN_VIEW_VIDEO_DEFAULT_SIZES,
  preload = 'none',
  waitForFullBuffer = false,
  preventLoadVideo = false,
  loop = false,
  nativeControlsOnAutoplayFailure = false,
  autoplayWithReducedMotion = false,
  startMode = 'visible',
  visibilityThreshold,
  inViewOptions,
  onClick,
  onProgress,
  onVideoEnded,
  onPlay,
  onPause,
  onPlaying,
  onWaiting,
  onSeeking,
  onSeeked,
  onError,
  onPlayRejected,
  onInViewChange,
  onVisibilityChange,
  testId,
  mediaTestId,
  poster,
  overlay,
  children
}, controlRef) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isPausedRef = useRef<boolean>(true);
  const pendingPlayRef = useRef<boolean>(false);
  const intentionalPauseRef = useRef<boolean>(false);
  const playbackIntentRef = useRef<boolean>(false);
  const unexpectedPauseRetryRef = useRef<boolean>(false);
  const wasExternallyPausedRef = useRef<boolean>(paused);
  const onInViewChangeRef = useRef(onInViewChange);
  const onVisibilityChangeRef = useRef(onVisibilityChange);
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isManualPlayback, setIsManualPlayback] = useState<boolean>(false);
  const [isPlaybackRequested, setIsPlaybackRequested] = useState<boolean>(false);
  const [hasEnded, setHasEnded] = useState<boolean>(false);
  const configuredThreshold = visibilityThreshold ?? getSpartanVideoVisibilityThreshold(startMode);
  const resolvedThreshold = inViewOptions?.threshold ?? clampVisibilityThreshold(configuredThreshold);
  const {
    ref: setInViewRef,
    inView
  } = useInView({
    ...SPARTAN_IN_VIEW_VIDEO_IN_VIEW_OPTIONS,
    ...inViewOptions,
    threshold: resolvedThreshold
  });
  const {
    ref: setVisibilityRef,
    inView: isVisible,
    entry: visibilityEntry
  } = useInView({
    ...SPARTAN_IN_VIEW_VIDEO_IN_VIEW_OPTIONS,
    ...inViewOptions,
    threshold: VISIBILITY_RATIO_THRESHOLDS
  });
  const setWrapperRef = useCallback((node: HTMLDivElement | null) => {
    setInViewRef(node);
    setVisibilityRef(node);
  }, [setInViewRef, setVisibilityRef]);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)') ?? false;
  const isPageActive = usePageActivity();
  const isTopNavigationMediaPaused = useSpartanProductTopNavigationMediaPause();
  const resolvedTestId = testId ?? `${SPARTAN_IN_VIEW_VIDEO_TRACKING_PREFIX}-${index + 1}`;
  const shouldAutoplay = active && !isManualPlayback && startMode !== 'manual' && !paused && inView && isPageActive && !isTopNavigationMediaPaused && (!prefersReducedMotion || autoplayWithReducedMotion) && !hasEnded;
  const shouldPlayManually = active && isManualPlayback && !paused && isVisible && isPageActive && !isTopNavigationMediaPaused && !hasEnded;
  const shouldContinuePlayback = shouldAutoplay || shouldPlayManually;
  const resolvedPreload = shouldContinuePlayback ? 'auto' : preload;
  useLayoutEffect(() => {
    const hadPlaybackIntent = playbackIntentRef.current;
    playbackIntentRef.current = shouldContinuePlayback;
    if (!hadPlaybackIntent && shouldContinuePlayback) {
      unexpectedPauseRetryRef.current = false;
    }
  }, [shouldContinuePlayback]);
  useLayoutEffect(() => {
    const wasExternallyPaused = wasExternallyPausedRef.current;
    wasExternallyPausedRef.current = paused;
    if (!wasExternallyPaused || paused || !active || !hasEnded) return;
    const videoEl = videoRef.current;
    if (videoEl?.ended) videoEl.currentTime = 0;
    setHasEnded(false);
  }, [active, hasEnded, paused]);
  useEffect(() => {
    onInViewChangeRef.current = onInViewChange;
    onVisibilityChangeRef.current = onVisibilityChange;
  }, [onInViewChange, onVisibilityChange]);
  useEffect(() => {
    onInViewChangeRef.current?.(inView);
  }, [inView]);
  useEffect(() => {
    const targetRect = visibilityEntry?.boundingClientRect;
    const rootRect = visibilityEntry?.rootBounds;
    const centerOffset = targetRect && rootRect ? targetRect.top + targetRect.height / 2 - (rootRect.top + rootRect.height / 2) : 0;
    onVisibilityChangeRef.current?.(isVisible, isVisible ? visibilityEntry?.intersectionRatio ?? 0 : 0, centerOffset);
  }, [isVisible, visibilityEntry]);
  const setPausedState = useCallback((nextPaused: boolean) => {
    const wasPaused = isPausedRef.current;
    isPausedRef.current = nextPaused;
    setIsPaused(nextPaused);
    if (wasPaused === nextPaused) return;
    if (nextPaused) {
      safeCall(onPause);
      return;
    }
    safeCall(onPlay);
  }, [onPause, onPlay]);
  const playVideo = useCallback((videoEl_0: HTMLVideoElement) => {
    setHasEnded(false);
    if (videoEl_0.ended) {
      videoEl_0.currentTime = 0;
    }
    pendingPlayRef.current = true;
    setIsPlaybackRequested(true);
    playVideoWithPromiseHandle(videoEl_0, undefined, () => {
      pendingPlayRef.current = false;
      setIsPlaybackRequested(false);
      setPausedState(true);
      safeCall(onPlayRejected);
    });
  }, [onPlayRejected, setPausedState]);
  const play = useCallback(() => {
    setHasEnded(false);
    setIsManualPlayback(true);
    pendingPlayRef.current = true;
    intentionalPauseRef.current = false;
    unexpectedPauseRetryRef.current = false;
    setIsPlaybackRequested(true);
    const videoEl_1 = videoRef.current;
    if (!videoEl_1 || !isReady || !active || !isVisible || !isPageActive) return;
    playVideo(videoEl_1);
  }, [active, isPageActive, isReady, isVisible, playVideo]);
  const playManually = useCallback(() => {
    setHasEnded(false);
    setIsManualPlayback(true);
    intentionalPauseRef.current = false;
    unexpectedPauseRetryRef.current = false;
    setIsPlaybackRequested(true);
  }, []);
  const pauseVideo = useCallback((videoEl_2: HTMLVideoElement): boolean => {
    if (videoEl_2.paused) return false;
    intentionalPauseRef.current = true;
    videoEl_2.pause();
    return true;
  }, []);
  const pause = useCallback(() => {
    pendingPlayRef.current = false;
    setIsManualPlayback(false);
    setIsPlaybackRequested(false);
    const videoEl_3 = videoRef.current;
    if (videoEl_3 && pauseVideo(videoEl_3)) return;
    setPausedState(true);
  }, [pauseVideo, setPausedState]);
  const pauseForEligibility = useCallback(() => {
    pendingPlayRef.current = false;
    setIsPlaybackRequested(false);
    const videoEl_4 = videoRef.current;
    if (videoEl_4 && pauseVideo(videoEl_4)) return;
    setPausedState(true);
  }, [pauseVideo, setPausedState]);
  useLayoutEffect(() => {
    if (active) return;
    pendingPlayRef.current = false;
    setHasEnded(false);
    setIsManualPlayback(false);
    setIsPlaybackRequested(false);
    unexpectedPauseRetryRef.current = false;
    const videoEl_5 = videoRef.current;
    if (videoEl_5) pauseVideo(videoEl_5);
    // Keep the outgoing frame stable while the carousel slide is still visible.
    if (videoEl_5 && !isVisible && videoEl_5.currentTime !== 0) {
      videoEl_5.currentTime = 0;
    }
    isPausedRef.current = true;
    setIsPaused(true);
  }, [active, isVisible, pauseVideo]);
  const handleReady = useCallback((video?: HTMLVideoElement) => {
    if (!video) return;
    videoRef.current = video;
    setIsReady(true);
    if (pendingPlayRef.current && active && isVisible && isPageActive) {
      playVideo(video);
      return;
    }
    if (shouldAutoplay || shouldPlayManually) return;
    pauseVideo(video);
    isPausedRef.current = true;
    setIsPaused(true);
  }, [active, isPageActive, isVisible, pauseVideo, playVideo, shouldAutoplay, shouldPlayManually]);
  useImperativeHandle(controlRef, () => ({
    play,
    pause
  }), [pause, play]);
  const handleEnded = useCallback(() => {
    pendingPlayRef.current = false;
    unexpectedPauseRetryRef.current = false;
    setHasEnded(true);
    setIsManualPlayback(false);
    setIsPlaybackRequested(false);
    if (onProgress && onVideoEnded) {
      // Keep the completed progress visible until the carousel advances.
      isPausedRef.current = true;
      setIsPaused(true);
    } else {
      pause();
    }
    safeCall(onVideoEnded);
  }, [onProgress, onVideoEnded, pause]);
  const handleVideoPlay = useCallback(() => {
    pendingPlayRef.current = false;
    setIsPlaybackRequested(true);
  }, []);
  const handleVideoPlaying = useCallback(() => {
    pendingPlayRef.current = false;
    intentionalPauseRef.current = false;
    unexpectedPauseRetryRef.current = false;
    setIsPlaybackRequested(true);
    setPausedState(false);
    safeCall(onPlaying);
  }, [onPlaying, setPausedState]);
  const handleVideoPause = useCallback(() => {
    pendingPlayRef.current = false;
    const wasIntentional = intentionalPauseRef.current;
    intentionalPauseRef.current = false;
    const videoEl_6 = videoRef.current;
    const shouldRetry = !wasIntentional && playbackIntentRef.current && !!videoEl_6 && !videoEl_6.ended && !unexpectedPauseRetryRef.current;
    if (!shouldRetry) {
      setIsPlaybackRequested(false);
      setPausedState(true);
      return;
    }
    unexpectedPauseRetryRef.current = true;
    setIsPlaybackRequested(true);
    playVideo(videoEl_6);
  }, [playVideo, setPausedState]);
  const handleVideoWaiting = useCallback(() => {
    safeCall(onWaiting);
  }, [onWaiting]);
  const handleVideoSeeking = useCallback(() => {
    safeCall(onSeeking);
  }, [onSeeking]);
  const handleVideoSeeked = useCallback(() => {
    safeCall(onSeeked);
  }, [onSeeked]);
  const handleVideoError = useCallback(() => {
    pendingPlayRef.current = false;
    setIsPlaybackRequested(false);
    setPausedState(true);
    safeCall(onError);
  }, [onError, setPausedState]);
  useEffect(() => {
    if (!isReady) return;
    if (isManualPlayback) {
      if (shouldPlayManually) {
        const videoEl_7 = videoRef.current;
        if (videoEl_7?.paused) playVideo(videoEl_7);
        return;
      }
      pauseForEligibility();
      return;
    }
    if (shouldAutoplay) {
      setIsPlaybackRequested(true);
      return;
    }
    pauseForEligibility();
  }, [inView, isManualPlayback, isReady, pauseForEligibility, playVideo, shouldAutoplay, shouldPlayManually]);
  return <div ref={setWrapperRef} className={cn('relative overflow-hidden', wrapperClassName)} onClick={onClick} {...createDataAttributes({
    test: resolvedTestId
  })}>
      <AppVideo className={className} video={src} desktopMedia={desktopMedia} poster={poster ?? {
      src: image.src,
      alt: image.alt,
      sizes
    }} autoplay={shouldAutoplay || shouldPlayManually} paused={paused || !active || isTopNavigationMediaPaused} loop={loop} nativeControlsOnAutoplayFailure={nativeControlsOnAutoplayFailure} preload={resolvedPreload} waitForFullBuffer={waitForFullBuffer} preventLoadVideo={preventLoadVideo} onReady={handleReady} onProgress={onProgress} onVideoEnded={handleEnded} onVideoPlay={handleVideoPlay} onVideoPlaying={handleVideoPlaying} onVideoPause={handleVideoPause} onVideoWaiting={handleVideoWaiting} onVideoSeeking={handleVideoSeeking} onVideoSeeked={handleVideoSeeked} onVideoError={handleVideoError} onPlayRejected={handleVideoError} testId={mediaTestId ?? `${resolvedTestId}-media`} />
      {overlay}
      {children?.({
      isInView: inView,
      isPaused: isPaused && !isPlaybackRequested,
      play: playManually,
      pause
    })}
    </div>;
});